<template>
  <div class="cf-flex cf-flex--column cf-flex--gap-16">
    <div class="cf-flex cf-flex--column cf-flex--gap-16">
      <div class="cf-flex cf-flex--row cf-flex--gap-12">
        <div class="cf-user-avatar cf-user-avatar--brand-primary">
          {{ order.groupOrder.you.displayNameInitials }}
        </div>

        <h3 class="cf-title cf-title--3">
          {{ order.groupOrder.you.displayName }}

          <span class="cf-subtitle cf-subtitle--2">
            (Your Order)
          </span>
        </h3>
      </div>

      <template v-if="order.groupOrder.you.orderItems.length > 0">
        <cf-order-items
          :hide-prices="selectedLocation.features.HidePrices"
          :disable-favoriting="true"
          :hide-totals="true"
          :hide-quantity="true"
          :order="order"
          :order-items="order.groupOrder.you.orderItems"
          :outside-borders="true"
          :read-only="order.groupOrder.isLocked"
        />
      </template>

      <!-- Host Empty Cart Cta -->
      <template v-else-if="order.groupOrder.you.orderItems.length === 0 && !order.groupOrder.isLocked">
        <h4 class="cf-title cf-title--4">
          You haven't added any items yet!
        </h4>

        <p class="cf-subtitle cf-subtitle--2">
          Browse our menu and start adding some tasty items to your cart.
        </p>

        <button
          class="cf-button cf-button--primary cf-button--md"
          type="button"
          @click="browseMenu"
        >
          Browse Menu
        </button>
      </template>
    </div>

    <!-- Guest Order Items -->
    <cf-expandable-section>
      <template #title>
        <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
          See what others are ordering
        </p>
      </template>

      <template #content>
        <div
          v-if="order.groupOrder.otherMembers.length === 0 && !order.groupOrder.isLocked"
          class="cf-flex cf-flex--column cf-flex--gap-16"
        >
          <h4 class="cf-title cf-title--4">
            Nobody has joined your group order yet.
          </h4>

          <button
            class="cf-button cf-button--secondary cf-button--md"
            type="button"
            @click="$openModal('editGroupOrder')"
          >
            Invite Friends
          </button>
        </div>

        <div
          v-for="(member, index) in order.groupOrder.otherMembers"
          :key="index"
          class="cf-flex cf-flex--column cf-flex--gap-16"
        >
          <div class="cf-flex cf-flex--row cf-flex--gap-12">
            <div class="cf-user-avatar">
              {{ member.displayNameInitials }}
            </div>

            <h3 class="cf-title cf-title--3 cf-align-self-center">
              {{ member.displayName }}

              <span
                v-if="member.isHost"
                class="cf-subtitle cf-subtitle--2"
              >
                (Host)
              </span>
            </h3>

            <button
              v-if="order.groupOrder.you.isHost"
              class="cf-title cf-title--5 cf-title--brand-primary cf-cursor-pointer"
              type="button"
              @click="removeMemberFromOrder(member)"
            >
              Remove
            </button>
          </div>

          <cf-order-items
            v-if="member.orderItems.length > 0"
            :hide-prices="selectedLocation.features.HidePrices"
            :disable-favoriting="true"
            :hide-totals="true"
            :hide-quantity="true"
            :order="order"
            :order-items="member.orderItems"
            :outside-borders="true"
            :read-only="order.groupOrder.isLocked"
          />
        </div>
      </template>
    </cf-expandable-section>

    <div
      v-if="order.groupOrder.you.isHost && !orderValidations.isValid"
      class="section section--half-y-padding"
    >
      <div class="message message--danger">
        <p
          v-for="error in orderValidations.errors"
          :key="error"
        >
          {{ error }}
        </p>
      </div>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';

  import CfOrderItems from '@/view/components/globals/cf-order-items/index.vue';
  import CfExpandableSection from '@/view/components/shared/cf-expandable-section/index.vue';


  export default {
    name: 'GroupOrderDetails',

    components: {
      CfOrderItems,
      CfExpandableSection
    },

    data() {
      return {
        isUnlockingOrder: false
      };
    },

    computed: {
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('orders', ['order', 'orderValidations'])
    },

    created() {
      this.createdHandler();
    },

    methods: {
      browseMenu() {
        this.$closeModal('cart');

        this.$router.push({
          name: 'orderMenu',
          params: {
            merchantId: this.$route.params.merchantId
          }
        });
      },

      cancelOrder() {
        this.$openModal('confirmation', {
          ctaConfirmText: 'Yes, Cancel',
          primaryText: 'All items and guests will be removed from your cart.',
          onConfirm: () => this.$store.dispatch('orders/cancelOrder'),
          onConfirmSuccess: () => {
            this.$closeAllModals();
          },
          title: 'Cancel group order?'
        });
      },

      confirmGroupOrderLock() {
        this.$openModal('confirmation', {
          ctaConfirmText: 'Lock Order',
          primaryText: 'By proceeding to checkout, your group\'s order will be locked - not allowing further changes. You can exit the checkout process to unlock your order.',
          onConfirm: () => this.$store.dispatch('orders/groupOrderUpdateLock', { isLocked: true }),
          title: 'Lock group order?'
        });
      },

      createdHandler() {
        this.loadUpdatedOrder();
      },

      leaveOrder() {
        this.$openModal('confirmation', {
          ctaConfirmText: 'Yes, Leave',
          primaryText: 'Your items in the group cart will be removed and your cart will be cleared.',
          onConfirm: () => this.$store.dispatch('orders/groupOrderLeave'),
          onConfirmSuccess: () => {
            this.$closeModal('cart');

            // this.$triggerGoogleTrackingEvent('GuestLeavesGroupOrder', 'GroupOrder');

            this.$triggerNewRelicEvent('GuestLeavesGroupOrder', 'Information', 'Guest Left Group Order');
          },
          title: 'Leave group order?'
        });
      },

      loadUpdatedOrder() {
        this.$store.dispatch('orders/getOrder', {
          silent: true
        });
      },

      removeMemberFromOrder(member) {
        this.$openModal('confirmation', {
          ctaConfirmText: 'Yes, Remove',
          primaryText: `${member.displayName} will be removed from the group. All of their items will be removed from the cart.`,
          onConfirm: () => this.$store.dispatch('orders/groupOrderRemoveMember', { member }),
          onConfirmSuccess: () => {
            // this.$triggerGoogleTrackingEvent('RemoveGuestFromGroupOrder', 'GroupOrder');

            this.$triggerNewRelicEvent('RemoveGuestFromGroupOrder', 'Information', 'Removed Guest From Group Order');
          },
          title: 'Remove from group?'
        });
      }
    }
  };
</script>
